import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { userAPI, roleAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { ROLE_INFO } from '@/context/AuthContext';
import { toast } from 'sonner';
import { UserCog, Loader2, Plus, Search, Pencil, UserX, Shield, Check, X } from 'lucide-react';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [disableUserId, setDisableUserId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'lop_manager',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        userAPI.getAll(),
        roleAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data.roles || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', password: '', role: 'lop_manager' });
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (user) => {
    setEditingUser(user);
    setFormData({ email: user.email, name: user.name, password: '', role: user.role });
    setAddDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (editingUser) {
        const updatePayload = {
          name: formData.name,
          role: formData.role,
        };
        // Only include password if it was changed
        if (formData.password && formData.password.trim() !== '') {
          updatePayload.password = formData.password;
        }
        await userAPI.update(editingUser.id, updatePayload);
        toast.success('User updated successfully');
      } else {
        await userAPI.create({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          role: formData.role,
        });
        toast.success('User created successfully');
      }
      setAddDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    try {
      await userAPI.disable(disableUserId);
      toast.success('User disabled successfully');
      setDisableUserId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable user');
    }
  };

  const getRoleColor = (role) => {
    const info = ROLE_INFO[role];
    if (!info) return 'bg-gray-100 text-gray-700';
    const colors = {
      red: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
      amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
      green: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
    };
    return colors[info.color] || 'bg-gray-100 text-gray-700';
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div data-testid="users-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage system users and their roles</p>
          </div>
          <Button onClick={handleOpenAddDialog} data-testid="add-user-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Role Legend */}
        <div className="flex flex-wrap gap-2 p-4 bg-muted/30 rounded-lg">
          {Object.entries(ROLE_INFO).map(([role, info]) => (
            <div key={role} className="flex items-center gap-2">
              <Badge className={getRoleColor(role)}>
                <Shield className="h-3 w-3 mr-1" />
                {info.label}
              </Badge>
              <span className="text-xs text-muted-foreground hidden sm:inline">{info.description}</span>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>System Users</CardTitle>
            <CardDescription>{users.length} users total</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <UserCog className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(user => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={getRoleColor(user.role)}>
                            <Shield className="h-3 w-3 mr-1" />
                            {ROLE_INFO[user.role]?.label || user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {user.is_active !== false ? (
                            <Badge variant="outline" className="border-emerald-500 text-emerald-600">
                              <Check className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-red-500 text-red-600">
                              <X className="h-3 w-3 mr-1" />
                              Disabled
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenEditDialog(user)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {user.is_active !== false && (
                              <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => setDisableUserId(user.id)}>
                                <UserX className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Update user details, role, or password' : 'Create a new system user'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {!editingUser && (
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" placeholder="user@example.com" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="user-email-input" />
                </div>
              )}
              {editingUser && (
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} disabled className="bg-muted" />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Name</Label>
                <Input placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
                <Input 
                  type="password" 
                  placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"} 
                  value={formData.password} 
                  onChange={(e) => setFormData({...formData, password: e.target.value})} 
                  data-testid="user-password-input"
                />
                {editingUser && (
                  <p className="text-xs text-muted-foreground">Only fill this if you want to change the user's password</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          <Badge className={getRoleColor(role.id)} variant="secondary">
                            {role.label}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.role && ROLE_INFO[formData.role] && (
                  <p className="text-xs text-muted-foreground">{ROLE_INFO[formData.role].description}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="user-submit-btn">
                {submitting ? 'Saving...' : editingUser ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Disable Confirmation */}
        <AlertDialog open={!!disableUserId} onOpenChange={() => setDisableUserId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disable User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to disable this user? They will no longer be able to log in.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDisable} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Disable User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
